import { useEffect } from 'react';

import { Form } from 'antd';

import { SCHEDULE_TYPES } from '@src/constants/commons';
import { ScheduleTemplateSelector } from '@src/containers/ScheduleTemplateSelector';
import SchedulesSelector from '@src/packages/dashboard/screens/SchedulesDashboard/components/ScheduleSelector';

import { INITIAL_DISABLE_SCHEDULE_TYPE } from '../../constants';
import { FilterData } from './FilterData';

/**
 * Combined component for selecting schedule type and corresponding schedules or templates.
 * Handles mutual exclusivity between schedulesIds and templateIds.
 *
 * @param {Object} props
 * @param {string|null} props.type - Current type value from form watch
 * @param {Object} [props.disabledTypes] - Types to disable in FilterData
 * @param {Object} props.selectedSchedules - Selected schedules state { byId: {}, selectedAll: boolean }
 * @param {Function} props.setSelectedSchedules - Setter for selectedSchedules
 * @param {Function} props.setLoadingSchedules - Setter for schedules loading state
 * @param {string[]} props.divisionIds - Division IDs to filter by
 * @param {string[]} props.initialScheduleIds - Initial schedule IDs for editing
 * @param {string} props.orgId - Organization ID
 * @param {string[]} [props.templateIds] - Initial template IDs for editing
 * @param {Function} [props.onTypeChange] - Callback when type changes (optional)
 * @param {Object} props.form - Ant Design form instance
 * @param {boolean} [props.showLabel] - Whether to show type label (default: false)
 */
export const DataTypeSelector = ({
  type,
  disabledTypes = INITIAL_DISABLE_SCHEDULE_TYPE,
  selectedSchedules,
  setSelectedSchedules,
  setLoadingSchedules,
  divisionIds,
  initialScheduleIds,
  orgId,
  templateIds = [],
  onTypeChange,
  form,
  showLabel = false,
  filterDataOptions = [],
}) => {
  const handleFilterChange = e => {
    const newType = e?.target?.value;
    form.setFieldsValue({
      type: newType,
      schedulesIds: [],
      templateIds: [],
    });
    setSelectedSchedules({ byId: {}, selectedAll: true });
    onTypeChange?.();
  };

  const renderOptions = () => {
    if (type === null) return null;
    switch (type) {
      case SCHEDULE_TYPES.schedule:
      case SCHEDULE_TYPES.checkpoint_route:
        return (
          <Form.Item name="schedulesIds">
            <SchedulesSelector
              selectedSchedules={selectedSchedules}
              type={type || SCHEDULE_TYPES.task}
              setSelected={setSelectedSchedules}
              setLoading={setLoadingSchedules}
              divisionIds={divisionIds}
              initialScheduleIds={initialScheduleIds}
              getPopupContainer={trigger => trigger.parentNode}
            />
          </Form.Item>
        );
      case SCHEDULE_TYPES.task:
        return (
          <Form.Item name="templateIds">
            <ScheduleTemplateSelector
              organizationId={orgId}
              divisionIds={divisionIds}
              templateIds={templateIds}
            />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (form) {
      const ids = selectedSchedules.selectedAll ? [] : Object.keys(selectedSchedules.byId);
      form.setFieldsValue({ schedulesIds: ids });
    }
  }, [selectedSchedules, form]);

  return (
    <>
      <Form.Item name="type" noStyle={!showLabel}>
        <FilterData
          value={type}
          disabledTypes={disabledTypes}
          onChange={handleFilterChange}
          filterDataOptions={filterDataOptions}
        />
      </Form.Item>

      <div className="gx-mt-2">{renderOptions()}</div>
    </>
  );
};

export default DataTypeSelector;
